import React from "react";
import { motion } from "framer-motion";
import { Camera, Loader2, MapPin, AlertCircle, X, CheckCircle2 } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

/**
 * Mandatory photo + GPS proof modal for driver clock-in / clock-out.
 *
 * Props:
 *   kind:    "clock_in" | "clock_out"
 *   jobId:   string
 *   jobTitle: string
 *   onClose: () => void
 *   onDone:  () => Promise<void>   // called after successful upload
 */
export default function ClockInOutModal({ kind, jobId, jobTitle, onClose, onDone }) {
  const inputRef = React.useRef(null);
  const [photo, setPhoto] = React.useState(null);          // { dataUrl, mime, name }
  const [coords, setCoords] = React.useState(null);         // { lat, lng, accuracy }
  const [gpsError, setGpsError] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [gpsLoading, setGpsLoading] = React.useState(false);

  const isIn = kind === "clock_in";
  const title = isIn ? "Clock in — photo proof" : "Clock out — photo proof";
  const cta = isIn ? "Clock in & start job" : "Clock out & mark ready";
  const endpointPath = isIn ? "start" : "mark-ready";

  // Auto-request GPS on mount
  React.useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError("Your device doesn't support GPS. Continue without location.");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        setGpsError(`Couldn't get GPS (${err.message}). You can still upload the photo.`);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setSubmitError("Photo too large (max 8 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto({ dataUrl: reader.result, mime: file.type, name: file.name });
      setSubmitError("");
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!photo) {
      setSubmitError("Take a photo first.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      // Strip the data: prefix — backend handles either form
      const file_data = String(photo.dataUrl).split(",")[1] || photo.dataUrl;
      await apiClient.post(`/driver/jobs/${jobId}/${endpointPath}`, {
        file_data,
        file_mime: photo.mime,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        accuracy_m: coords?.accuracy ?? null,
      });
      await onDone();
    } catch (err) {
      setSubmitError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="clock-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              {isIn ? "Step 1 · Arrival" : "Step 2 · Completion"}
            </p>
            <h3 className="font-display mt-2 text-xl font-bold tracking-tight text-[#0A0A0A]">
              {title}
            </h3>
            <p className="mt-1 text-xs text-[#475569] line-clamp-1">{jobTitle}</p>
          </div>
          <button onClick={onClose} disabled={submitting} data-testid="clock-close" className="p-2 hover:bg-[#F5F5F5] disabled:opacity-50"><X size={18} /></button>
        </div>

        {/* Photo capture */}
        <div className="mt-5">
          <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
            {isIn ? "Photo of the pickup location" : "Photo of the completed job"}
          </label>
          {photo ? (
            <div className="relative" data-testid="clock-photo-preview">
              <img src={photo.dataUrl} alt="proof" className="w-full max-h-64 object-cover border border-[#0A0A0A]/10" />
              <button
                onClick={() => { setPhoto(null); inputRef.current && (inputRef.current.value = ""); }}
                className="absolute top-2 right-2 bg-black/70 text-white p-2 hover:bg-black"
                disabled={submitting}
                data-testid="clock-photo-retake"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={submitting}
              data-testid="clock-photo-button"
              className="w-full flex flex-col items-center justify-center gap-2 py-8 bg-[#F5F5F5] border-2 border-dashed border-[#0A0A0A]/20 hover:border-[#1E4D55] transition-colors disabled:opacity-60"
            >
              <Camera size={28} className="text-[#1E4D55]" />
              <span className="text-sm font-semibold text-[#0A0A0A]">Take photo</span>
              <span className="text-[11px] text-[#475569]">Camera will open · used as timestamped proof</span>
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPick}
            className="hidden"
            data-testid="clock-photo-input"
          />
        </div>

        {/* GPS state */}
        <div className="mt-4 flex items-start gap-2 text-xs">
          <MapPin size={14} className="text-[#1E4D55] mt-0.5 shrink-0" />
          <div className="flex-1">
            {gpsLoading && <span className="text-[#475569] inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Getting your location…</span>}
            {!gpsLoading && coords && (
              <span data-testid="clock-gps-ok" className="text-[#2c8642] inline-flex items-center gap-1">
                <CheckCircle2 size={12} /> Location locked · ±{Math.round(coords.accuracy)}m
              </span>
            )}
            {!gpsLoading && gpsError && (
              <span data-testid="clock-gps-error" className="text-[#A66800]">{gpsError}</span>
            )}
          </div>
        </div>

        {submitError && (
          <div data-testid="clock-error" className="mt-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{submitError}</span>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} disabled={submitting} data-testid="clock-cancel" className="px-4 py-3 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A] disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !photo}
            data-testid="clock-submit"
            className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {submitting ? "Uploading…" : cta}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
